import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import RffInputWrapper from "../shared/RffInputWrapper";
import AgGridTableWrapper from "../../agGrid/AgGridTableWrapper";
import { AgGridReact } from "ag-grid-react";
import { useForm } from "react-final-form";

function getIn(obj, path) {
  if (!obj) return undefined;
  const segments = path.split(".");
  let current = obj;
  for (const segment of segments) {
    if (current == null) return undefined;
    const bracketMatch = segment.match(/^([^\[]+)\[(\d+)\]$/);
    if (bracketMatch) {
      const [_, arrayKey, idx] = bracketMatch;
      current = current[arrayKey];
      if (!Array.isArray(current) || current[+idx] === undefined)
        return undefined;
      current = current[+idx];
    } else {
      current = current[segment];
    }
  }
  return current;
}

function buildPath(prefix, rowName, columnField) {
  return prefix
    ? `${prefix}.${rowName}.${columnField}`
    : `${rowName}.${columnField}`;
}

const RffLoanBorrowerExperienceTable = ({ name, label, sx }) => {
  const form = useForm();
  const previousValuesRef = useRef({});

  const initialRowData = useMemo(
    () => [
      {
        description: "Property fix and flips completed and sold",
        less_than_three_years: 0,
        greater_than_three_years: 0,
        name: "ff_sold",
      },
      {
        description: "Property rehabs completed and retained as rental",
        less_than_three_years: 0,
        greater_than_three_years: 0,
        name: "ff_retained_as_rental",
      },
      {
        description: "Income Producing Properties acquired",
        less_than_three_years: 0,
        greater_than_three_years: 0,
        name: "income_producing_properties",
      },
      {
        description: "Ground up construction - completed and sold",
        less_than_three_years: 0,
        greater_than_three_years: 0,
        name: "guc_sold",
      },
      {
        description: "Ground up construction and retained as rental",
        less_than_three_years: 0,
        greater_than_three_years: 0,
        name: "guc_retained_as_rental",
      },
      {
        description:
          "Property rehab completed and sold as GC for 3rd party investor",
        less_than_three_years: 0,
        greater_than_three_years: 0,
        name: "general_contractor_projects",
      },
    ],
    []
  );

  const getFormValuesForRow = useCallback(
    (rowName, values) => {
      if (name) {
        const parent = getIn(values, name) || {};
        return parent[rowName] || {};
      }
      return values[rowName] || {};
    },
    [name]
  );

  const initializeRowData = useCallback(
    (values) => {
      return initialRowData.map((row) => {
        const fv = getFormValuesForRow(row.name, values);
        return {
          ...row,
          less_than_three_years:
            fv.less_than_three_years ?? row.less_than_three_years,
          greater_than_three_years:
            fv.greater_than_three_years ?? row.greater_than_three_years,
        };
      });
    },
    [initialRowData, getFormValuesForRow]
  );

  const [rowData, setRowData] = useState(() => {
    const values = form.getState().values || {};
    return initializeRowData(values);
  });

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Real Estate Investment Experience:",
        // headerName: "Include borrower previous experience with:",
        field: "description",
        flex: 1,
        minWidth: 160,
        suppressSizeToFit: true,
        cellStyle: {
          backgroundColor: "#f5f5f5",
          borderRight: "1px solid #ddd",
          paddingTop: "10px",
          paddingBottom: "10px",
        },
      },
      {
        headerName: "Less than 3 years",
        field: "less_than_three_years",
        minWidth: 85,
        maxWidth: 110,
        flex: 1,
        editable: true,
        cellStyle: {
          backgroundColor: "#fff",
          textAlign: "center",
          borderRight: "1px solid #ddd",
        },
        valueParser: (p) => Number(p.newValue),
      },
      {
        headerName: "Greater than 3 years",
        field: "greater_than_three_years",
        maxWidth: 125,
        minWidth: 85,
        flex: 1,
        editable: true,
        cellStyle: {
          backgroundColor: "#fff",
          textAlign: "center",
          borderRight: "1px solid #ddd",
        },
        valueParser: (p) => Number(p.newValue),
      },
      { headerName: "Name", field: "name", hide: true },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: false,
      sortable: false,
      wrapText: true,
      autoHeight: true,
      autoHeaderHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        fontSize: "13px",
        paddingLeft: "10px",
        display: "flex",
        alignItems: "center",
        textAlign: "left",
        paddingRight: "10px",
      },
      headerClass: "ag-header-cell-wrap",
    }),
    []
  );

  const onCellValueChanged = useCallback(
    (params) => {
      const { data, colDef, newValue } = params;
      const field = colDef.field;
      if (
        ["less_than_three_years", "greater_than_three_years"].includes(field)
      ) {
        const path = buildPath(name, data.name, field);
        const old = getIn(form.getState().values, path);
        const num = Number(newValue);
        if (old !== num) {
          form.change(path, num);
          setRowData((rd) =>
            rd.map((r) => (r.name === data.name ? { ...r, [field]: num } : r))
          );
        }
      }
    },
    [form, name]
  );

  const getRowStyle = useCallback((props) => {
    return {
      borderBottom: "1px solid #ddd",
    };
  }, []);

  // Extract relevant values for this component
  const getRelevantValues = useCallback(
    (values) => {
      const relevant = {};
      initialRowData.forEach((row) => {
        const rowValues = getFormValuesForRow(row.name, values);
        relevant[row.name] = {
          less_than_three_years: rowValues.less_than_three_years,
          greater_than_three_years: rowValues.greater_than_three_years,
        };
      });
      return relevant;
    },
    [initialRowData, getFormValuesForRow]
  );

  useEffect(() => {
    // Initialize form values
    const vals = form.getState().values || {};
    initialRowData.forEach((r) => {
      ["less_than_three_years", "greater_than_three_years"].forEach((f) => {
        const p = buildPath(name, r.name, f);
        if (getIn(vals, p) === undefined) form.change(p, r[f]);
      });
    });

    // Store initial relevant values
    previousValuesRef.current = getRelevantValues(vals);

    // Subscribe only to value changes
    const unsubscribe = form.subscribe(
      ({ values }) => {
        if (!values) return;

        // Get current relevant values
        const currentRelevantValues = getRelevantValues(values);

        // Check if relevant values have changed
        const hasChanged =
          JSON.stringify(currentRelevantValues) !==
          JSON.stringify(previousValuesRef.current);

        if (hasChanged) {
          // Update row data only if relevant values changed
          setRowData(initializeRowData(values));
          previousValuesRef.current = currentRelevantValues;
        }
      },
      { values: true }
    );

    return unsubscribe;
  }, [form, name, initialRowData, getRelevantValues, initializeRowData]);

  return (
    <RffInputWrapper
      size={12}
      label={label}
      sx={{
        ".ag-header": { border: "none !important" },
        ".ag-header-cell": {
          paddingLeft: "4px",
        },
        ".ag-header-cell.ag-column-first": {
          ".ag-header-cell-text": {
            fontSize: "13px",
            color: "rgba(0, 0, 0, 0.9)",
            fontStyle: "normal",
          },
        },
        ".ag-header-cell-wrap .ag-header-cell-text": {
          whiteSpace: "normal !important",
          overflow: "visible !important",
          textOverflow: "unset !important",
          lineHeight: "1.2 !important",
          fontStyle: "italic",
          // fontSize: "13px",
          fontSize: "11px",
        },
        ".ag-cell": {
          whiteSpace: "normal !important",
          lineHeight: "1.2 !important",
          borderRight: "none !important",
        },
        ".ag-body": {
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #ddd",
        },
        ...sx,
      }}
    >
      <AgGridTableWrapper
        simpleTable
        suppressBorder
        noBorder
        // sx={{ mx: "-3px", width: "calc(100% + 6px)" }}
        // sx={{ borderRadius: "8px", overflow: "hidden" }}
        size={4}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onCellValueChanged={onCellValueChanged}
          headerHeight={30}
          getRowStyle={getRowStyle}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          suppressMovableColumns
          domLayout="autoHeight"
          key={JSON.stringify(rowData)}
        />
      </AgGridTableWrapper>
    </RffInputWrapper>
  );
};

export default RffLoanBorrowerExperienceTable;
